import cn from 'classnames'
import {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styles from './allLocations.module.scss'
import { LocationsType, StateType } from '@/types'

interface IBreedCountry {
  dogs: StateType[] | undefined
  countryName?: string
  inputText: string
  breedersLocation?: LocationsType
}

const BreedCountry = memo(({ dogs, countryName }: IBreedCountry) => {
  return (
    <>
      {countryName && dogs.length !== 0 && <h3>{countryName}</h3>}
      <div className={styles.breedStates}>
        {dogs.map((dog, index) => (
          <div
            key={index}
            className={dog.count ? styles.match : styles.noMatch}
          >
            <a href={`/puppies-for-sale/${dog.slug}`}>{dog.state}</a>
          </div>
        ))}
      </div>
    </>
  )
})

const sortByCount = (a, b) => b.count - a.count

interface IRelatedStates {
  breedersLocation:
    | {
        locations: LocationsType[]
      }
    | undefined
  inputText: string
  hasSearch: boolean
}

const RelatedStates = memo(
  ({ breedersLocation, inputText, hasSearch }: IRelatedStates) => {
    const [showAll, setShowAll] = useState(false)
    const [usa, setUsa] = useState(
      (breedersLocation?.locations?.[0]?.states || []).sort(sortByCount)
    )
    const [canada, setCanada] = useState(
      (breedersLocation?.locations?.[1]?.states || []).sort(sortByCount)
    )

    // Filter states with dogs
    const usaMatch = useMemo(
      () => usa.filter((state) => state.count !== 0),
      [usa]
    )

    const canadaMatch = useMemo(
      () => canada.filter((state) => state.count !== 0),
      [canada]
    )

    useEffect(() => {
      if ((inputText || '').length > 0 && hasSearch) {
        setUsa(
          breedersLocation?.locations?.[0]?.states
            .sort(sortByCount)
            .filter((state) => state.state.toLowerCase().includes(inputText))
        )
        setCanada(
          breedersLocation?.locations?.[1]?.states
            .sort(sortByCount)
            .filter((state) => state.state.toLowerCase().includes(inputText))
        )
      } else if ((inputText || '').length === 0 && hasSearch) {
        setUsa(breedersLocation?.locations?.[0]?.states.sort(sortByCount))
        setCanada(breedersLocation?.locations?.[1]?.states.sort(sortByCount))
      }
    }, [
      showAll,
      inputText,
      hasSearch,
      breedersLocation?.locations?.[1],
      breedersLocation?.locations?.[0],
    ])

    const handleSetShow = useCallback(() => {
      setShowAll(!showAll)
    }, [setShowAll, showAll])

    return (
      <div className={styles.relatedStatesContainer}>
        {hasSearch && usa.length === 0 && canada.length === 0 && (
          <div className={styles.noResult}>
            <p>
              We have no locations that match this description, please search
              again!
            </p>
          </div>
        )}

        <BreedCountry
          dogs={showAll || hasSearch ? usa : usaMatch}
          inputText={inputText}
        />

        <BreedCountry
          dogs={showAll || hasSearch ? canada : canadaMatch}
          countryName="Canada"
          inputText={inputText}
        />

        {!hasSearch && (
          <div onClick={handleSetShow} className={styles.view}>
            <p>
              {showAll ? 'View less locations' : 'View all locations'}{' '}
              <span>
                <img
                  width="11px"
                  height="6px"
                  alt={showAll ? 'Black up arrow' : 'Black down arrow'}
                  src={
                    showAll
                      ? '/search/arrow-top-black.svg'
                      : '/search/arrow-down-black.svg'
                  }
                />
              </span>
            </p>
          </div>
        )}
      </div>
    )
  }
)

interface IAllLocations {
  breedersLocation: {
    locations: LocationsType[]
  }
  inputText?: string
  hasSearch: boolean
}

export default function AllLocations({
  breedersLocation,
  hasSearch,
}: IAllLocations) {
  const [inputText, setInputText] = useState('')

  return (
    <div className={cn(styles.article, styles.marginBottom)}>
      {hasSearch ? (
        <div className={styles.titleWrapper}>
          <h2 className={styles.title}> Explore by Location </h2>
          <input
            type="search"
            placeholder="Search State or Province"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setInputText(e.target.value.toLowerCase())
            }
          />
        </div>
      ) : (
        <p className={styles.title}> Explore by Location </p>
      )}

      {!!breedersLocation && (
        <RelatedStates
          breedersLocation={breedersLocation}
          inputText={inputText}
          hasSearch={hasSearch}
        />
      )}
    </div>
  )
}
